import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Zap,
  Check,
  Clock,
  AlertCircle,
  FlaskConical,
  TrendingUp,
} from 'lucide-react';
import { deliveryService } from '@services/delivery.service';
import type { DemoDispatchParams } from '@services/delivery.service';
import { PageGuard } from '@components/PageGuard';
import { cn } from '@lib/utils';

// ── Page ──────────────────────────────────────────────────────────────────────

export const DemoProviderPage = () => {
  const [acceptanceRate, setAcceptanceRate]     = useState(80);
  const [responseTime, setResponseTime]         = useState<DemoDispatchParams['simulatedResponseTime']>('30s');
  const [failureRate, setFailureRate]           = useState(5);
  const [lastDispatch, setLastDispatch]         = useState<{
    outcome: string;
    responseTimeMs: number;
    triggeredAt: string;
  } | null>(null);

  const qc = useQueryClient();

  const dispatchMutation = useMutation({
    mutationFn: () =>
      deliveryService.triggerDemoDispatch({
        simulatedAcceptanceRate: acceptanceRate,
        simulatedResponseTime: responseTime,
        simulatedFailureRate: failureRate,
      }),
    onSuccess: (result) => {
      setLastDispatch({
        outcome:        result.outcome,
        responseTimeMs: result.responseTimeMs,
        triggeredAt:    new Date().toLocaleTimeString(),
      });
      // Immediately refresh the history table so the new row appears without a reload.
      qc.invalidateQueries({ queryKey: ['delivery', 'demo', 'dispatches'] });
    },
  });

  const { data: recentDispatches, isLoading: dispatchesLoading } = useQuery({
    queryKey: ['delivery', 'demo', 'dispatches'],
    queryFn: () => deliveryService.listDemoDispatches(),
    refetchInterval: 5_000,   // poll every 5 s so dispatches triggered elsewhere also appear
  });

  const outcomeChip: Record<string, string> = {
    accepted: 'bg-[var(--state-success-bg)] text-[var(--state-success)]',
    rejected: 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]',
    timeout:  'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
  };

  return (
    <PageGuard permission="settings.read">
      <div className="min-h-screen bg-[var(--bg-base)] p-8 space-y-8">
        {/* Back link */}
        <Link
          to="/delivery/providers"
          className="inline-flex items-center gap-2 text-[13px] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Back to Providers
        </Link>

        {/* Page header */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[var(--chart-6)] flex items-center justify-center text-white font-bold text-[22px]">
            D
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
                Demo Provider
              </h1>
              <span className="rounded-full bg-[var(--bg-elevated)] text-[var(--text-accent)] text-[11px] font-semibold px-2.5 py-1">
                Sandbox
              </span>
            </div>
            <p className="text-[14px] text-[var(--text-muted)]">
              Simulated delivery provider for testing dispatch flows without real API calls
            </p>
          </div>
        </div>

        {/* Info banner */}
        <div className="rounded-2xl bg-[var(--bg-elevated)] border border-[var(--border-default)] p-4 flex items-start gap-3">
          <FlaskConical className="w-5 h-5 text-[var(--text-accent)] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            <p className="text-[14px] font-semibold text-[var(--text-primary)]">Sandbox only</p>
            <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
              This provider always responds with simulated outcomes. No real riders, orders, or
              payments are involved. Use it to test the full broadcast + assignment flow in
              development or staging.
            </p>
          </div>
        </div>

        {/* Settings */}
        <div className="rounded-3xl bg-white shadow-soft p-8 space-y-6">
          <div className="border-b border-[var(--border-subtle)] pb-5">
            <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Simulation Settings</h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
              Control how the demo provider behaves when dispatches are triggered
            </p>
          </div>

          {/* Acceptance Rate slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
                Simulated acceptance rate
              </label>
              <span className="text-[14px] font-semibold text-[var(--text-primary)]">{acceptanceRate}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              value={acceptanceRate}
              onChange={(e) => setAcceptanceRate(Number(e.target.value))}
              className="w-full accent-[var(--brand-leaf)]"
            />
            <div className="flex justify-between mt-1">
              <span className="text-[11px] text-[var(--text-muted)]">0% (always reject)</span>
              <span className="text-[11px] text-[var(--text-muted)]">100% (always accept)</span>
            </div>
          </div>

          {/* Response time */}
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)] mb-2 block">
              Simulated response time
            </label>
            <select
              value={responseTime}
              onChange={(e) =>
                setResponseTime(e.target.value as DemoDispatchParams['simulatedResponseTime'])
              }
              className="w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] focus:border-[var(--border-strong)] transition-colors sm:max-w-xs"
            >
              <option value="instant">Instant (~100ms)</option>
              <option value="30s">30 seconds</option>
              <option value="60s">60 seconds</option>
              <option value="random">Random (1–90 seconds)</option>
            </select>
            <p className="text-[12px] text-[var(--text-muted)] mt-1.5">
              How quickly the simulated rider responds to a broadcast
            </p>
          </div>

          {/* Failure rate slider */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-muted)]">
                Simulated failure rate
              </label>
              <span className="text-[14px] font-semibold text-[var(--text-primary)]">{failureRate}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={50}
              value={failureRate}
              onChange={(e) => setFailureRate(Number(e.target.value))}
              className="w-full accent-[var(--state-danger)]"
            />
            <p className="text-[12px] text-[var(--text-muted)] mt-1.5">
              Percentage of dispatches that simulate a system error (tests error handling paths)
            </p>
          </div>
        </div>

        {/* Dispatch button */}
        <div className="rounded-3xl bg-white shadow-soft p-8 space-y-5">
          <div className="border-b border-[var(--border-subtle)] pb-5">
            <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Test Dispatch</h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
              Trigger a fake broadcast using the settings above
            </p>
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            <button
              onClick={() => dispatchMutation.mutate()}
              disabled={dispatchMutation.isPending}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--chart-6)] px-6 py-3 text-[14px] font-semibold text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 hover:brightness-110 hover:shadow-[0_12px_32px_rgba(124,179,66,0.45)] active:brightness-95"
            >
              <Zap className="w-4 h-4" strokeWidth={1.5} />
              {dispatchMutation.isPending ? 'Dispatching...' : 'Trigger Demo Dispatch'}
            </button>

            {dispatchMutation.isError && (
              <div className="flex items-center gap-2 text-[13px] text-[var(--state-danger)]">
                <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
                Dispatch failed. Check Delivery Service logs
              </div>
            )}
          </div>

          {lastDispatch && (
            <div
              className={cn(
                'rounded-2xl p-4 flex items-start gap-3 text-[13px]',
                lastDispatch.outcome === 'accepted'
                  ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]'
                  : lastDispatch.outcome === 'rejected'
                  ? 'bg-[var(--state-danger-bg)] text-[var(--state-danger)]'
                  : 'bg-[var(--state-warning-bg)] text-[var(--state-warning)]',
              )}
            >
              {lastDispatch.outcome === 'accepted' ? (
                <Check className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              ) : (
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              )}
              <div>
                <p className="font-semibold capitalize">{lastDispatch.outcome}</p>
                <p className="opacity-80">
                  Response in {lastDispatch.responseTimeMs}ms at {lastDispatch.triggeredAt}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Recent test dispatches */}
        <div className="rounded-3xl bg-white shadow-soft p-8">
          <div className="border-b border-[var(--border-subtle)] pb-5 mb-6">
            <h2 className="text-[20px] font-semibold text-[var(--text-primary)]">Recent Test Dispatches</h2>
            <p className="text-[14px] text-[var(--text-muted)] mt-0.5">
              Updates live, refreshing every 5 seconds
            </p>
          </div>

          {dispatchesLoading ? (
            <p className="text-[14px] text-[var(--text-muted)] py-6 text-center">Loading...</p>
          ) : !recentDispatches || recentDispatches.length === 0 ? (
            <div className="text-center py-10">
              <TrendingUp className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-3" strokeWidth={1.5} />
              <p className="text-[14px] text-[var(--text-muted)]">No demo dispatches yet.</p>
              <p className="text-[12px] text-[var(--text-muted)] mt-1">
                Click "Trigger Demo Dispatch" above to create one.
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-[var(--bg-base)]">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Triggered
                  </th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Outcome
                  </th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    Response Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border-subtle)]">
                {recentDispatches.map((d) => (
                  <tr key={d.id} className="hover:bg-[var(--bg-base)] transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-[13px] text-[var(--text-muted)]">
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {new Date(d.triggeredAt).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                          outcomeChip[d.outcome] ?? 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                        )}
                      >
                        {d.outcome}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[13px] text-[var(--text-primary)]">{d.responseTimeMs}ms</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </PageGuard>
  );
};
